import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import NotificationContainer from 'react-notifications/lib/NotificationContainer';

import EmailAddressForm from '../forms/EmailAddressForm';
import HorizontalDivider from '../components/common/HorizontalDivider';
import Button from '../components/common/Button';
import HotelLogo from '../components/common/HotelLogo';
import SectionHeader from '../components/common/SectionHeader';
import Text from '../components/common/Text';
import removeCancellationInvoices from '../utils/removeCancellationInvoices';
import {
  getInvoicesByReservationId,
  handleDownloadInvoice,
  sendInvoiceEmail,
} from '../redux/reducer/actions';

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uniqueId } = useParams();

  const [downloadSuccessful, setDownloadSuccessful] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [, setIsSendingEmail] = useState(false);
  const [sendEmailSuccessful, setSendEmailSuccessful] = useState(false);

  const [invoiceIds, setInvoiceIds] = useState(null);

  const reservation = useSelector((state) => state.app.reservation).data;
  const invoiceList = useSelector((state) => state.app.invoices).list;
  const { thankYouPageUrl } = useSelector((state) => state.app.urls);

  const formInitialValues = {
    email: reservation?.primaryGuest?.email || '',
  };

  useEffect(() => {
    if (reservation) {
      dispatch(
        getInvoicesByReservationId({
          hotelId: uniqueId,
          reservationId: reservation.id,
        }),
      );
    }
  }, [dispatch, reservation, uniqueId]);

  useEffect(() => {
    if (invoiceList && invoiceList.length > 0) {
      const filteredInvoices = removeCancellationInvoices(invoiceList);
      const filteredInvoiceIds = filteredInvoices.map((invoice) => invoice.id);
      setInvoiceIds(filteredInvoiceIds);
    }
  }, [invoiceList]);

  const handleDownload = async () => {
    setIsDownloading(true);

    await dispatch(handleDownloadInvoice({ hotelId: uniqueId, invoiceIds }))
      .then((response) => {
        if (response.payload) {
          response.payload.forEach((payload) => {
            const linkSource = `data:application/pdf;base64,${payload}`;
            const link = document.createElement('a');
            link.href = linkSource;
            link.download = `Invoice-${new Date().toISOString()}.pdf`;
            link.click();
          });
          setIsDownloading(false);
          setDownloadSuccessful(true);
          NotificationManager.success(
            'Success: Invoice(s) downloaded to your device',
            'Notification',
            3000,
          );
        }
      })
      .catch(() => {
        setDownloadSuccessful(false);
        NotificationManager.error(
          'Error while downloading invoice(s)',
          'Notification',
          3000,
        );
      });
  };

  // TODO: Update UI when form is submitted, and email is being sent / successfully sent
  const handleFormSubmit = async (data) => {
    setIsSendingEmail(true);

    const result = await dispatch(
      sendInvoiceEmail({
        to: data.email,
        fullName: `${reservation.primaryGuest.firstName} ${reservation.primaryGuest.lastName}`,
        subject: `Your invoice from ${
          reservation.property.name
        } at: ${new Date().toLocaleDateString()}`,
        hotelName: reservation.property.name,
        hotelId: uniqueId,
        invoiceIds,
      }),
    );

    if (result?.payload === 'Sending mail Failed') {
      setSendEmailSuccessful(false);
      NotificationManager.error(
        'Error while emailing invoice(s)',
        'Notification',
        3000,
      );
    } else if (result && result.payload) {
      console.log(result);
      setSendEmailSuccessful(true);
      setIsSendingEmail(false);
      NotificationManager.success(
        'Success: Invoice(s) sent to your email',
        'Notification',
        3000,
      );
    } else {
      setSendEmailSuccessful(false);
      NotificationManager.error(
        'Error while emailing invoice(s)',
        'Notification',
        3000,
      );
    }
  };

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <HotelLogo />
      <section className="flex flex-col gap-8">
        <SectionHeader text="Checkout complete" />
        <div className="flex flex-col gap-8">
          <Text>Please select how you want to receive your invoice.</Text>
        </div>

        {downloadSuccessful ? (
          <Text>Invoices downloaded successfully</Text>
        ) : (
          <div className="flex flex-col gap-4">
            <Text>Download PDF files to your device:</Text>
            <Button
              variant="secondary"
              text="Download invoice(s)"
              type="button"
              onClick={handleDownload}
              loading={isDownloading}
            />
          </div>
        )}

        <HorizontalDivider text="or" />

        <div className="flex flex-col gap-4">
          <Text>Send via email:</Text>
          <EmailAddressForm
            initialValues={formInitialValues}
            onSubmit={handleFormSubmit}
            setIsSendingEmail={setIsSendingEmail}
            // sendEmailSuccessful={sendEmailSuccessful}
          />
          <Button
            variant="primary"
            text="Continue"
            type="button"
            onClick={() => navigate(thankYouPageUrl)}
            disabled={!downloadSuccessful && !sendEmailSuccessful}
          />
        </div>
      </section>
      <NotificationContainer />
    </main>
  );
};

export default CheckoutSuccessPage;
